import React, {useState} from "react";
import {Tabs} from "antd";
import {useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {TICKET_SCOPES, TICKET_STATUSES_BY_STATE} from "constants/ticket";
import {TicketList} from "../common/TicketList";
import {TicketCategoryTabs} from "../common/TicketCategoryTabs";
import {useTicketOptions} from "../common/useTicketOptions";
import {TicketDetailModal} from "../detail/TicketDetailModal";

const segments = [TICKET_SCOPES.mine, TICKET_SCOPES.assignedToMe, TICKET_SCOPES.claimable];

export const TicketManagementPage = () => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const history = useHistory();
    const location = useLocation();
    const {roleOptions, roleLabels} = useTicketOptions();
    // Başvuru detayındaki talep başlığı bağlantısı (?ticketId=) ilgili talebin detayını doğrudan açar
    const linkedTicketId = new URLSearchParams(location.search).get("ticketId");
    const [scope, setScope] = useState(TICKET_SCOPES.assignedToMe);
    const [category, setCategory] = useState("courier");
    const [refreshKey, setRefreshKey] = useState(0);

    // Bağlantıyla açılan talebin bulunduğu kapsam ve kategori sekmesi seçilir; modal kapandığında kullanıcı
    // o sekmede kalır
    useFetch({
        fetchFn: stores.ticketStore.getTicket.bind(null, linkedTicketId),
        defaultDataValue: null,
        condition: !!linkedTicketId,
        dependencies: [linkedTicketId],
        callbackFunc: response => {
            const detail = response?.data;
            if (!detail) {
                return;
            }
            const {ticket, history} = detail;
            const isQueuedForRole =
                ticket.assignedTo?.type === "role" &&
                TICKET_STATUSES_BY_STATE.open.includes(ticket.status);
            const isAssignedToMe = history.some(
                item => item.kind === "assign" && item.userId === stores.meStore.userId
            );
            if (isQueuedForRole) {
                setScope(TICKET_SCOPES.claimable);
            } else if (isAssignedToMe) {
                setScope(TICKET_SCOPES.assignedToMe);
            } else if (ticket.createdBy?._id === stores.meStore.userId) {
                setScope(TICKET_SCOPES.mine);
            }
            if (ticket.ticketType?.category) {
                setCategory(ticket.ticketType.category);
            }
        }
    });

    const [, mineCounts] = useFetch({
        fetchFn: stores.ticketStore.getTicketCounts.bind(null, {scope: TICKET_SCOPES.mine}),
        defaultDataValue: {},
        dependencies: [refreshKey]
    });
    const [, assignedToMeCounts] = useFetch({
        fetchFn: stores.ticketStore.getTicketCounts.bind(null, {scope: TICKET_SCOPES.assignedToMe}),
        defaultDataValue: {},
        dependencies: [refreshKey]
    });
    const [, claimableCounts] = useFetch({
        fetchFn: stores.ticketStore.getTicketCounts.bind(null, {scope: TICKET_SCOPES.claimable}),
        defaultDataValue: {},
        dependencies: [refreshKey]
    });
    const countsByScope = {
        [TICKET_SCOPES.mine]: mineCounts?.data,
        [TICKET_SCOPES.assignedToMe]: assignedToMeCounts?.data,
        [TICKET_SCOPES.claimable]: claimableCounts?.data
    };

    return (
        <div style={{padding: "24px"}}>
            <h2>{t("management.title", {ns: "tickets"})}</h2>
            <Tabs activeKey={scope} onChange={setScope} type="card">
                {segments.map(segment => (
                    <Tabs.TabPane
                        key={segment}
                        tab={`${t(`management.segments.${segment}`, {
                            ns: "tickets"
                        })} (${countsByScope[segment]?.total ?? 0})`}
                    />
                ))}
            </Tabs>
            <TicketCategoryTabs
                category={category}
                counts={countsByScope[scope]?.byCategory}
                onChange={setCategory}
            />
            <TicketList
                key={`${scope}-${category}`}
                scope={scope}
                category={category}
                readOnlyDetail={scope === TICKET_SCOPES.mine}
                roleOptions={roleOptions}
                roleLabels={roleLabels}
                refreshKey={refreshKey}
                onChanged={() => setRefreshKey(previous => previous + 1)}
            />
            <TicketDetailModal
                ticketId={linkedTicketId}
                roleOptions={roleOptions}
                roleLabels={roleLabels}
                onClose={() => history.replace("/ticket-management")}
                onChanged={() => setRefreshKey(previous => previous + 1)}
            />
        </div>
    );
};
